import React, { useEffect, useState, useContext, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { BsClock, BsLayers, BsSlashCircle, BsSpeedometer2, BsArrowLeft } from 'react-icons/bs';
import PageHeader from 'Utils/PageHeader';
import FetchContainer from 'Utils/FetchContainer';
import { engineLabels, EngineIcon } from 'Utils';
import { getSourceNomenclature } from 'Utils/nomenclature';
import AppContext from 'Utils/AppContext';
import { readableJobError } from 'Secure/api';
import { normalize, fmtNum, fmtDuration, fmtTime } from './normalize';
import TableDetails from './TableDetails';
import { JOB_RESULT_VARIANTS } from './variants';
import { BRAND, INK, STATUS } from 'Utils/styleTokens';

const card = {
  borderRadius: 12, border: `1px solid ${INK[150]}`, background: '#fff',
  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
};

function statusTone(status) {
  const s = String(status || '').toUpperCase();
  if (['COMPLETED', 'SUCCEEDED', 'MASKED'].includes(s)) return { backgroundColor: STATUS.success.bg, color: STATUS.success.text };
  if (['FAILED', 'ERROR'].includes(s)) return { backgroundColor: STATUS.danger.bg, color: STATUS.danger.text };
  return { backgroundColor: STATUS.info.bg, color: STATUS.info.text };
}

function friendlyResultsError(e, verb) {
  const status = e && (e.status || e.statusCode);
  const v = verb || 'masking';
  // verb is past tense ("masked"); turn it into the gerund for "finished X" without a lookup table.
  const gerund = v === 'masked' ? 'masking' : v.endsWith('ed') ? `${v.slice(0, -2)}ing` : v;
  if (status === 409) return `These results will be available once the job has finished ${gerund}.`;
  if (status === 404) return 'No results found for this job.';
  return (e && e.message) || 'Could not load results.';
}

function SummaryCard({ icon, label, value, sub, tone }) {
  const ring = tone === 'bad' ? '#F6C9C9' : tone === 'good' ? '#BBF0CB' : INK[150];
  return (
    <div style={{ ...card, border: `1px solid ${ring}`, flex: 1, minWidth: 200, padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: INK[400] }}>
        {icon}{label}
      </div>
      <div style={{ marginTop: 6, fontSize: 24, fontWeight: 700, color: INK[900] }}>{value}</div>
      {sub && <div style={{ marginTop: 2, fontSize: 14, color: INK[500] }}>{sub}</div>}
    </div>
  );
}

function TimingCard({ data }) {
  const { queueMs, runMs, totalMs, payload } = data;
  const denom = totalMs > 0 ? totalMs : 1;
  const qPct = Math.max(2, (queueMs / denom) * 100);
  const rPct = Math.max(2, (runMs / denom) * 100);
  return (
    <div style={{ ...card, padding: 20 }}>
      {/* Panel title */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, fontWeight: 600, color: INK[700] }}>
        <BsClock size={16} color={INK[400]} /> Timing
      </div>
      {/* The 3 times */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, fontSize: 14, marginBottom: 16 }}>
        <div><div style={{ fontSize: 12, color: INK[400] }}>Submitted</div><div style={{ color: INK[700] }}>{fmtTime(payload.submittedAt)}</div></div>
        <div><div style={{ fontSize: 12, color: INK[400] }}>Started</div><div style={{ color: INK[700] }}>{fmtTime(payload.startedAt)}</div></div>
        <div><div style={{ fontSize: 12, color: INK[400] }}>Completed</div><div style={{ color: INK[700] }}>{fmtTime(payload.completedAt)}</div></div>
      </div>
      {/* The bar across — queue wait is neutral grey; the active run segment carries the mask violet. */}
      <div style={{ display: 'flex', height: 12, overflow: 'hidden', borderRadius: 999, background: INK[100], marginBottom: 8 }}>
        <div style={{ height: '100%', width: `${qPct}%`, background: INK[200] }} title={`Queue wait ${fmtDuration(queueMs)}`} />
        <div style={{ height: '100%', width: `${rPct}%`, background: BRAND.violet }} title={`Run time ${fmtDuration(runMs)}`} />
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 20px', fontSize: 12, color: INK[500] }}>
        <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 999, background: INK[200], marginRight: 6 }} />Queue wait <b style={{ color: INK[700] }}>{fmtDuration(queueMs)}</b></span>
        <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 999, background: BRAND.violet, marginRight: 6 }} />Run time <b style={{ color: INK[700] }}>{fmtDuration(runMs)}</b></span>
        <span>Total <b style={{ color: INK[700] }}>{fmtDuration(totalMs)}</b></span>
      </div>
    </div>
  );
}

const JobResults = ({ variant = 'masking' }) => {
  const cfg = JOB_RESULT_VARIANTS[variant] || JOB_RESULT_VARIANTS.masking;
  const { jobId } = useParams();
  const { connections } = useContext(AppContext);
  const [meta, setMeta] = useState(null);
  const [results, setResults] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [resultsError, setResultsError] = useState(null);

  useEffect(() => {
    const init = async () => {
      setFetching(true);
      setResultsError(null);
      setResults(null);
      const [metaRes, resultsRes] = await Promise.all([
        cfg.getJob(jobId).then((r) => ({ ok: r })).catch((e) => ({ err: e })),
        cfg.getResults(jobId).then((r) => ({ ok: r })).catch((e) => ({ err: e })),
      ]);
      setMeta(metaRes.ok || null);
      if (resultsRes.err) setResultsError(friendlyResultsError(resultsRes.err, cfg.verb));
      else setResults(resultsRes.ok);
      setFetching(false);
    };
    if (jobId) init();
    // cfg is derived from the static `variant` prop, so it's stable per mount.
  }, [jobId, variant]); // eslint-disable-line react-hooks/exhaustive-deps

  const data = useMemo(() => (results ? normalize(results) : null), [results]);

  const source = (connections || []).find((c) => c && meta && String(c.id) === String(meta.dataSourceId));
  const sourceName = (meta && meta.dataSourceName) || (source && source.name) || cfg.fallbackName(jobId);
  const engine = (meta && meta.engine) || (source && source.type) || '';
  const isFile = data && data.isFile;
  const isNoSql = data && data.isNoSql;
  // Unit/record nouns per modality: files/records, collections/documents, tables/rows.
  // The modality here comes from the results payload (isFile/isNoSql), not the connection —
  // map it to an engine sentinel so both nouns come from the single nomenclature source.
  const resultNouns = getSourceNomenclature(isFile ? 'file' : isNoSql ? 'mongodb' : 'postgresql');
  const unitNoun = resultNouns.itemSingular;
  const recordNoun = resultNouns.record;
  const status = data && !data.purged ? data.payload.status : (meta && meta.status);
  const backDataSourceId = (meta && meta.dataSourceId != null)
    ? meta.dataSourceId
    : (source && source.id != null ? source.id : null);
  // Cross-source destination(s) of this job: the fan-out list when present, else the
  // singular target, else none (in-place). Absent entirely on encrypt/decrypt jobs.
  const targetIds =
    meta && Array.isArray(meta.targetDataSourceIds) && meta.targetDataSourceIds.length > 0
      ? meta.targetDataSourceIds
      : meta && meta.targetDataSourceId != null
        ? [meta.targetDataSourceId]
        : [];
  const backLink = cfg.backLink(backDataSourceId);
  const HeaderIcon = cfg.icon;

  // Rows processed per minute over the active run time (queue wait excluded).
  let throughputPerMin = null;
  if (data && !data.purged && data.runMs > 0 && data.totals.masked > 0) {
    throughputPerMin = Math.round(data.totals.masked / (data.runMs / 60000));
  }

  // "no errors" / "N error(s)" appended to the top line once results are loaded.
  let errorSummaryLabel = '';
  let errorSummaryTone = INK[500];
  if (data && !data.purged) {
    const errs = data.totals.failedRows || data.totals.errors || 0;
    if (errs > 0) {
      errorSummaryLabel = `${fmtNum(errs)} error${errs === 1 ? '' : 's'}`;
      errorSummaryTone = STATUS.danger.text;
    } else {
      errorSummaryLabel = 'no errors';
      errorSummaryTone = STATUS.success.text;
    }
  }

  return (
    <FetchContainer fetchingData={fetching} absolute={true}>
      <div>
        {backLink && (
          <Link
            to={backLink.to}
            className='d-inline-flex align-items-center mb-2'
            style={{ fontSize: '14px' }}
          >
            <BsArrowLeft className='mr-1' /> {backLink.label}
          </Link>
        )}
        <div className='title-div'>
          <h5 className='text-left h-secondary'>{cfg.title}</h5>
          <PageHeader
            header={sourceName}
            tooltip={cfg.headerTooltip}
            icon={HeaderIcon}
          />
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, color: INK[500], fontSize: 14 }}>
            <span>Job #{jobId}</span>
            {data && !data.purged && (
              <>
                <span style={{ color: INK[200] }}>·</span>
                <span>{isFile ? 'File' : isNoSql ? 'NoSQL' : 'Database'} job</span>
              </>
            )}
            {engine && (
              <>
                <span style={{ color: INK[200] }}>·</span>
                {/* Engine chip doubles as a shortcut to this job's data source editor. */}
                {backDataSourceId != null ? (
                  <Link
                    to={`/sources/source-editor/${encodeURIComponent(backDataSourceId)}`}
                    title='Open the data source editor'
                    data-testid='results-engine-source-link'
                  >
                    <EngineIcon engine={engine} /> {engineLabels[engine] || engine}
                  </Link>
                ) : (
                  <span><EngineIcon engine={engine} /> {engineLabels[engine] || engine}</span>
                )}
              </>
            )}
            {targetIds.length > 0 && (
              <>
                <span style={{ color: INK[200] }}>·</span>
                {/* Cross-source destination(s). A fan-out is ONE job that wrote every listed
                    target in a single read+mask pass — all of them belong to this results page. */}
                <span data-testid='results-targets'>
                  →{' '}
                  {targetIds.map((tid, i) => {
                    const t = (connections || []).find((c) => c && String(c.id) === String(tid));
                    return (
                      <span key={tid}>
                        {i > 0 && <span style={{ color: INK[300] }}> · </span>}
                        <Link to={`/landscape/riskprofile/${encodeURIComponent(tid)}`}>
                          {t ? t.name : `#${tid}`}
                        </Link>
                      </span>
                    );
                  })}
                  {targetIds.length > 1 && (
                    <span style={{ color: INK[400] }}> ({targetIds.length}-target fan-out)</span>
                  )}
                </span>
              </>
            )}
            {status && (
              <>
                <span style={{ color: INK[200] }}>·</span>
                <span
                  style={{ ...statusTone(status), borderRadius: 999, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}
                  title={/FAILED|INTERRUPTED/i.test(String(status)) && meta ? readableJobError(meta) : undefined}
                >
                  {status}
                </span>
                <span style={{ color: errorSummaryTone, fontWeight: 600 }}>{errorSummaryLabel}</span>
              </>
            )}
          </div>
        </div>

        {resultsError ? (
          <div style={{ ...card, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '48px 16px', textAlign: 'center' }}>
            <BsSlashCircle size={36} color={INK[300]} style={{ marginBottom: 12 }} />
            <div style={{ fontSize: 16, fontWeight: 600, color: INK[700] }}>Results unavailable</div>
            <p style={{ marginTop: 6, maxWidth: 440, fontSize: 14, color: INK[500] }}>{resultsError}</p>
          </div>
        ) : data && data.purged ? (
          <div style={{ ...card, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '56px 16px', textAlign: 'center' }}>
            <BsSlashCircle size={40} color={INK[300]} style={{ marginBottom: 12 }} />
            <div style={{ fontSize: 18, fontWeight: 600, color: INK[700] }}>Results no longer available</div>
            <p style={{ marginTop: 6, maxWidth: 460, fontSize: 14, color: INK[500] }}>
              This job completed successfully, but its detailed results have been purged from the worker.
              Summary status is retained; the per-{unitNoun} breakdown is unavailable.
            </p>
          </div>
        ) : data ? (
          <>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
              <SummaryCard
                icon={<BsLayers size={13} style={{ marginRight: 4 }} />}
                label={cfg.summaryLabel}
                value={`${fmtNum(data.totals.masked)} ${recordNoun}`}
                sub={`${fmtNum(data.totals.fields)} ${isNoSql ? 'paths' : 'fields'} across ${data.units.length} ${unitNoun}${data.units.length === 1 ? '' : 's'}`}
              />
              <SummaryCard
                icon={<BsClock size={13} style={{ marginRight: 4 }} />}
                label='Duration'
                value={fmtDuration(data.totalMs)}
                sub={`${fmtDuration(data.runMs)} run · ${fmtDuration(data.queueMs)} queue`}
              />
              <SummaryCard
                icon={<BsSpeedometer2 size={13} style={{ marginRight: 4 }} />}
                label='Throughput'
                value={throughputPerMin != null ? `${fmtNum(throughputPerMin)}/min` : '—'}
                sub={`${recordNoun} ${cfg.verb} per minute`}
              />
            </div>

            <div style={{ marginBottom: 16 }}><TimingCard data={data} /></div>

            <TableDetails units={data.units} isFile={isFile} isNoSql={isNoSql} verb={cfg.verb} />
          </>
        ) : null}
      </div>
    </FetchContainer>
  );
};

// Thin per-operation wrappers over the shared JobResults page. The existing masking route keeps its
// default import; encrypt/decrypt routes use the named wrappers.
const MaskingResults = () => <JobResults variant='masking' />;
export const EncryptResults = () => <JobResults variant='encrypt' />;
export const DecryptResults = () => <JobResults variant='decrypt' />;
export { JobResults };
export default MaskingResults;
